import { useState, useMemo } from 'react'
import type React from 'react'
import {
  CheckCircle, AlertCircle, XCircle, HelpCircle, RefreshCw,
  Search, Download, ChevronDown, ChevronRight,
  Cpu, Globe, Brain, Zap,
} from 'lucide-react'
import { usePlatformFeatures, useVerifyPlatformFeature } from '@/hooks/useApi'

type Cap = {
  platform_feature_id: string
  name: string
  description: string | null
  category: string
  criticality: string
  status: string
  visibility: string
  verification_status: string
  last_verified: string | null
  rto_minutes: number
  rpo_minutes: number
  updated_at: string
  owner_primary?: string
}

const REGISTRY_CATEGORIES = ['skill', 'integration', 'model', 'feature'] as const
type RegistryCategory = typeof REGISTRY_CATEGORIES[number]

const CATEGORY_META: Record<RegistryCategory, { label: string; Icon: React.ComponentType<{ className?: string }> }> = {
  skill: { label: 'Skills & Plugins', Icon: Cpu },
  integration: { label: 'Integrations', Icon: Globe },
  model: { label: 'Models', Icon: Brain },
  feature: { label: 'Platform Features', Icon: Zap },
}

const VERIFICATION: Record<string, { label: string; color: string; Icon: React.ComponentType<{ className?: string }> }> = {
  verified: { label: 'Verified', color: 'text-green-400', Icon: CheckCircle },
  warning: { label: 'Warning', color: 'text-amber-400', Icon: AlertCircle },
  failed: { label: 'Failed', color: 'text-red-400', Icon: XCircle },
  unknown: { label: 'Unknown', color: 'text-white/30', Icon: HelpCircle },
  pending: { label: 'Pending', color: 'text-blue-400', Icon: RefreshCw },
}

const CRIT_COLORS: Record<string, string> = {
  critical: 'text-red-400 bg-red-400/10',
  high: 'text-amber-400 bg-amber-400/10',
  medium: 'text-blue-400 bg-blue-400/10',
  low: 'text-white/30 bg-white/5',
}

const STATUS_COLORS: Record<string, string> = {
  active: 'text-green-400',
  experimental: 'text-blue-400',
  deprecated: 'text-amber-400',
  planned: 'text-white/30',
  retired: 'text-white/20',
  removed: 'text-red-400/60',
}

function exportCSV(caps: Cap[], category: string) {
  const header = 'name,category,criticality,status,verification_status,last_verified,owner'
  const rows = caps.map(c =>
    [
      `"${c.name}"`, c.category, c.criticality, c.status,
      c.verification_status, c.last_verified || '', `"${c.owner_primary || ''}"`,
    ].join(',')
  )
  const csv = [header, ...rows].join('\n')
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `skills-registry-${category}-${new Date().toISOString().slice(0, 10)}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

function CapRow({ cap, onVerify, verifying }: { cap: Cap; onVerify: () => void; verifying: boolean }) {
  const [open, setOpen] = useState(false)
  const v = VERIFICATION[cap.verification_status] || VERIFICATION.unknown
  return (
    <div className="border-b border-white/5 last:border-0">
      <button
        className="w-full flex items-center gap-2 pl-6 pr-4 py-2 hover:bg-white/[0.03] text-left transition-colors"
        onClick={() => setOpen(o => !o)}
      >
        {open
          ? <ChevronDown className="w-3 h-3 text-white/20 shrink-0" />
          : <ChevronRight className="w-3 h-3 text-white/20 shrink-0" />}
        <span className="flex-1 text-[11px] font-mono text-white/80 truncate min-w-0">{cap.name}</span>
        <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded shrink-0 ${CRIT_COLORS[cap.criticality] || 'text-white/30 bg-white/5'}`}>
          {cap.criticality}
        </span>
        <span className={`text-[10px] font-mono shrink-0 w-20 text-right hidden sm:block ${STATUS_COLORS[cap.status] || 'text-white/30'}`}>
          {cap.status}
        </span>
        <span className={`flex items-center gap-1 text-[10px] font-mono shrink-0 w-20 justify-end ${v.color}`}>
          <v.Icon className="w-3 h-3" />
          <span className="hidden sm:inline">{v.label}</span>
        </span>
      </button>
      {open && (
        <div className="pl-12 pr-4 pb-3 pt-1 space-y-1.5 bg-white/[0.01]">
          {cap.description && (
            <p className="text-[11px] text-white/50 leading-relaxed">{cap.description}</p>
          )}
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-[10px] text-white/40 font-mono">
            <span>visibility: <span className="text-white/60">{cap.visibility}</span></span>
            {cap.owner_primary && <span>owner: <span className="text-white/60">{cap.owner_primary}</span></span>}
            {cap.last_verified && (
              <span>verified: <span className="text-white/60">{new Date(cap.last_verified).toLocaleString()}</span></span>
            )}
            {cap.rto_minutes > 0 && <span>RTO: <span className="text-white/60">{cap.rto_minutes}m</span></span>}
          </div>
          <button
            onClick={e => { e.stopPropagation(); onVerify() }}
            disabled={verifying}
            className="text-[10px] px-2.5 py-1 rounded bg-white/5 hover:bg-white/10 text-white/60 hover:text-white/90 transition-colors disabled:opacity-40 font-mono"
          >
            {verifying ? 'Verifying…' : 'Verify Now'}
          </button>
        </div>
      )}
    </div>
  )
}

function CategoryGroup({
  category, caps, openGroups, onToggle, onVerify, verifying
}: {
  category: RegistryCategory
  caps: Cap[]
  openGroups: Set<string>
  onToggle: (c: string) => void
  onVerify: (id: string) => void
  verifying: string | null
}) {
  const meta = CATEGORY_META[category]
  const Icon = meta.Icon
  const isOpen = openGroups.has(category)
  if (caps.length === 0) return null
  return (
    <div>
      <button
        className="w-full flex items-center gap-2 px-4 py-2 hover:bg-white/[0.02] transition-colors sticky top-0 bg-[#0a0e1a]/95 backdrop-blur z-10"
        onClick={() => onToggle(category)}
      >
        <Icon className="w-3.5 h-3.5 text-white/30 shrink-0" />
        <span className="text-[10px] font-bold uppercase tracking-widest text-white/40">{meta.label}</span>
        <span className="text-[10px] font-mono text-white/20 ml-1">{caps.length}</span>
        <div className="flex-1" />
        {isOpen
          ? <ChevronDown className="w-3 h-3 text-white/20" />
          : <ChevronRight className="w-3 h-3 text-white/20" />}
      </button>
      {isOpen && caps.map(cap => (
        <CapRow
          key={cap.platform_feature_id}
          cap={cap}
          onVerify={() => onVerify(cap.platform_feature_id)}
          verifying={verifying === cap.platform_feature_id}
        />
      ))}
    </div>
  )
}

export function SkillsRegistryPanel() {
  const [search, setSearch] = useState('')
  const [verifying, setVerifying] = useState<string | null>(null)
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set(REGISTRY_CATEGORIES))
  const { data, isLoading, error } = usePlatformFeatures()
  const verifyMutation = useVerifyPlatformFeature()

  const grouped = useMemo(() => {
    const all = (data?.platform_features as Cap[]) || []
    const filtered = all.filter(c => {
      if (!REGISTRY_CATEGORIES.includes(c.category as RegistryCategory)) return false
      if (search && !c.name.toLowerCase().includes(search.toLowerCase())) return false
      return true
    })
    const result: Record<RegistryCategory, Cap[]> = { skill: [], integration: [], model: [], feature: [] }
    for (const cap of filtered) {
      if (cap.category in result) result[cap.category as RegistryCategory].push(cap)
    }
    return result
  }, [data, search])

  const totalVisible = Object.values(grouped).reduce((s, a) => s + a.length, 0)
  const totalAll = (data?.platform_features as Cap[] | undefined)?.filter(
    c => REGISTRY_CATEGORIES.includes(c.category as RegistryCategory)
  ).length ?? 0

  const toggleGroup = (cat: string) => {
    setOpenGroups(prev => {
      const next = new Set(prev)
      if (next.has(cat)) next.delete(cat)
      else next.add(cat)
      return next
    })
  }

  const handleVerify = async (id: string) => {
    setVerifying(id)
    try { await verifyMutation.mutateAsync(id) } finally { setVerifying(null) }
  }

  const allCaps = (data?.platform_features as Cap[]) || []
  const registryCaps = allCaps.filter(c => REGISTRY_CATEGORIES.includes(c.category as RegistryCategory))

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 pt-4 pb-3 border-b border-white/5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Brain className="w-4 h-4 text-white/40" />
            <h2 className="text-xs font-bold text-white/70 uppercase tracking-widest">Skills & Models</h2>
          </div>
          <div className="flex items-center gap-2">
            {data && (
              <span className="text-[10px] font-mono text-white/30">{totalVisible}/{totalAll}</span>
            )}
            {registryCaps.length > 0 && (
              <button
                onClick={() => exportCSV(registryCaps, 'all')}
                className="flex items-center gap-1 text-[10px] px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-white/40 hover:text-white/70 transition-colors font-mono"
                title="Export as CSV"
              >
                <Download className="w-3 h-3" />CSV
              </button>
            )}
          </div>
        </div>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-white/20" />
          <input
            type="text"
            placeholder="Search skills, integrations, models, features…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-6 pr-2 py-1 text-[11px] font-mono bg-white/5 border border-white/10 rounded text-white/70 placeholder:text-white/20 focus:outline-none focus:border-white/20"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading && (
          <div className="px-4 py-8 text-center text-xs text-white/30 font-mono">Loading skills registry…</div>
        )}
        {error && (
          <div className="px-4 py-8 text-center text-xs text-white/30 font-mono">Skills registry pending backend deployment</div>
        )}
        {!isLoading && !error && totalAll === 0 && (
          <div className="px-4 py-8 text-center text-xs text-white/30 font-mono">No skills or integrations found</div>
        )}
        {!isLoading && !error && totalAll > 0 && (
          REGISTRY_CATEGORIES.map(cat => (
            <CategoryGroup
              key={cat}
              category={cat}
              caps={grouped[cat]}
              openGroups={openGroups}
              onToggle={toggleGroup}
              onVerify={handleVerify}
              verifying={verifying}
            />
          ))
        )}
      </div>
    </div>
  )
}
